import React, { useState } from 'react';
import { createSharedQuiz, SharedQuizWritePayload } from '../../Game/api/gameApi';
import QuestionPicker from './QuestionPicker';

interface CustomQuizCreateModalProps {
  onClose: () => void;
  onSaved: () => void;
}

const CustomQuizCreateModal: React.FC<CustomQuizCreateModalProps> = ({ onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rounds, setRounds] = useState(1);
  const [questionsPerRound, setQuestionsPerRound] = useState(5);
  const [isActive, setIsActive] = useState(true);
  const [limitAttempts, setLimitAttempts] = useState(false);
  const [maxAttempts, setMaxAttempts] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [createdJoinCode, setCreatedJoinCode] = useState<string | null>(null);
  const [joinCode, setJoinCode] = useState('');

  const handleSave = async () => {
    if (!title.trim()) {
      setSaveError('Title is required.');
      return;
    }
    if (selectedIds.size === 0) {
      setSaveError('Select at least one question.');
      return;
    }

    const payload: SharedQuizWritePayload = {
      title: title.trim(),
      description: description.trim(),
      rounds,
      questions_per_round: questionsPerRound,
      is_active: isActive,
      max_attempts_per_user: limitAttempts ? maxAttempts : null,
      join_code: joinCode.trim(),
      selected_question_ids: Array.from(selectedIds),
    };

    try {
      setSaving(true);
      setSaveError(null);
      const created = await createSharedQuiz(payload);
      setCreatedJoinCode(created.join_code);
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to create custom game');
    } finally {
      setSaving(false);
    }
  };

  const baseInputCls = `w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Add Custom Game</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {createdJoinCode ? (
            // ── Success state ──
            <div className="text-center py-10">
              <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-gray-900 font-medium mb-1">Custom game created</p>
              <p className="text-sm text-gray-500 mb-4">Share this join code with participants:</p>
              <div className="inline-block px-6 py-3 bg-gray-100 rounded-lg font-mono text-2xl tracking-widest text-gray-900">
                {createdJoinCode}
              </div>
            </div>
          ) : (
            // ── Form ──
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={baseInputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Join Code</label>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  maxLength={30}
                  className={`${baseInputCls} font-mono uppercase`}
                />
                <p className="text-xs text-gray-400 mt-1">Letters and numbers only. Leave blank for a random 5-letter code.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={`${baseInputCls} resize-y`} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rounds</label>
                  <input type="number" min={1} value={rounds} onChange={(e) => setRounds(Math.max(1, Number(e.target.value)))} className={baseInputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Questions per Round</label>
                  <input type="number" min={1} value={questionsPerRound} onChange={(e) => setQuestionsPerRound(Math.max(1, Number(e.target.value)))} className={baseInputCls} />
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-700 mb-1">
                  <input type="checkbox" checked={limitAttempts} onChange={(e) => setLimitAttempts(e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
                  Limit attempts per user
                </label>
                {limitAttempts && (
                  <input type="number" min={1} value={maxAttempts} onChange={(e) => setMaxAttempts(Math.max(1, Number(e.target.value)))} className={`${baseInputCls} mt-1`} />
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
                  Active
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question Pool *</label>
                <QuestionPicker selectedIds={selectedIds} onChange={setSelectedIds} />
              </div>

              {saveError && <p className="text-sm text-red-600">{saveError}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex-shrink-0 flex justify-end gap-3">
          {createdJoinCode ? (
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Creating...' : 'Create'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomQuizCreateModal;